// Journal-style consolidation of inventory movements.
//
// Background: the /movements endpoint returns one row per inventory event,
// which means a busy event can produce thousands of "sale -1" rows the
// operator doesn't want to scroll through. This helper folds runs of sales
// (the `sale` reason only — release/adjust/restock/etc. stay as discrete
// admin entries) into a single "5 sales" journal row with a time range.
//
// Order is preserved: input is whatever order the API returns (today: newest
// first, by createdAt DESC); output is the same sequence with sale runs
// collapsed in place.

import type { InventoryMovement } from '../api/inventory';

export interface SaleRun {
  kind: 'sale_run';
  /** Stable key for React lists: ids of the first + last sale in the run. */
  key: string;
  count: number;
  /** Sum of deltas — typically negative (sales reduce stock). */
  totalDelta: number;
  /** Earliest timestamp in the run (ISO). */
  firstAt: string;
  /** Latest timestamp in the run (ISO). */
  lastAt: string;
}

export interface AdminEntry {
  kind: 'admin';
  movement: InventoryMovement;
}

export type JournalEntry = SaleRun | AdminEntry;

/**
 * Walk the movements list and collapse adjacent `sale` entries into a single
 * SaleRun. Adjacency is determined by the input order — caller is responsible
 * for sorting by createdAt before calling.
 *
 * Bookkeeping note: when the input is newest-first (the API default), the
 * first sale we encounter inside a run is the most recent (`lastAt`); we
 * overwrite `firstAt` on each subsequent iteration so it ends up holding the
 * oldest sale in the run by the time the run closes.
 */
export function consolidateMovements(movements: InventoryMovement[]): JournalEntry[] {
  const out: JournalEntry[] = [];
  let run: { ids: string[]; count: number; totalDelta: number; firstAt: string; lastAt: string } | null =
    null;

  const flushRun = () => {
    if (!run) return;
    const first = run.ids[0];
    const last = run.ids[run.ids.length - 1];
    out.push({
      kind: 'sale_run',
      key: `sales:${first}:${last}`,
      count: run.count,
      totalDelta: run.totalDelta,
      firstAt: run.firstAt,
      lastAt: run.lastAt,
    });
    run = null;
  };

  for (const m of movements) {
    if (m.reason === 'sale') {
      if (run === null) {
        run = {
          ids: [m.id],
          count: 1,
          totalDelta: m.delta,
          firstAt: m.createdAt,
          lastAt: m.createdAt,
        };
      } else {
        run.ids.push(m.id);
        run.count += 1;
        run.totalDelta += m.delta;
        // Caller passes newest-first; the second/third/... entry is older,
        // so it becomes the new `firstAt`. lastAt was set when the run opened
        // (the most recent sale).
        run.firstAt = m.createdAt;
      }
    } else {
      flushRun();
      out.push({ kind: 'admin', movement: m });
    }
  }
  flushRun();
  return out;
}
